import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import './ApplyV2Picture.scss'

const baseClass = 'ApplyV2Picture'

interface Source {
    srcSet: string
    type: string
    media?: string
}

interface ApplyV2PictureProps extends React.HTMLAttributes<HTMLPictureElement> {
    sources?: Source[]
    fallbackSrc: string
    alt: string
    imgClassName?: string
    loading?: 'lazy' | 'eager'
    width?: number | string
    height?: number | string
    sizes?: string
    imgSrcSet?: string
}

const ApplyV2Picture: React.FC<ApplyV2PictureProps> = ({
    sources = [],
    fallbackSrc,
    alt,
    imgClassName = '',
    loading = 'lazy',
    width,
    height,
    sizes,
    imgSrcSet,
    className = '',
    ...rest
}) => {
    const [isLoading, setIsLoading] = useState(true)

    const handleLoad = () => setIsLoading(false)
    const handleError = () => setIsLoading(false)

    const imgRef = useRef<HTMLImageElement | null>(null)

    useEffect(() => {
        if (imgRef.current?.complete) {
            setIsLoading(true)
        }
    }, [fallbackSrc])

    return (
        <div
            className={clsx(`${baseClass}__wrapper`, {
                [`${className}__wrapper`]: className,
                [`${baseClass}__wrapper--loading`]: isLoading,
                [`${className}__wrapper--loading`]: isLoading && className,
            })}
        >
            <picture
                className={clsx(baseClass, className, {
                    [`${baseClass}--loading`]: isLoading,
                    [`${className}--loading`]: isLoading && className,
                })}
                {...rest}
            >
                {sources.map((source, index) => (
                    <source key={index} srcSet={source.srcSet} type={source.type} media={source.media} />
                ))}
                <img
                    ref={imgRef}
                    src={fallbackSrc}
                    alt={alt}
                    className={clsx(imgClassName, `${baseClass}__img`, {
                        [`${className}__img`]: className,
                        [`${imgClassName}--loading`]: isLoading,
                        [`${baseClass}__img--loading`]: isLoading,
                        [`${className}__img--loading`]: isLoading && className,
                    })}
                    loading={loading}
                    width={width}
                    height={height}
                    sizes={sizes}
                    srcSet={imgSrcSet}
                    onLoad={handleLoad}
                    onError={handleError}
                />
            </picture>
            {isLoading && (
                <ApplySkeleton
                    isLoading={isLoading}
                    className={clsx(`${baseClass}__skeleton`, {
                        [`${className}__skeleton`]: className,
                    })}
                />
            )}
        </div>
    )
}

export default ApplyV2Picture
